// Contracts — kids PACK / clip upload to R2 (2026-06-30). After a moderator
// records a participant's appearance (Session V1) they upload the shot video
// clips to R2. ACTIVE flow = presigned PUT (variant A): the worker signs a
// short-lived URL and the browser PUTs the bytes STRAIGHT to R2 — bytes never
// pass through the worker (no 100 MB request-body cap, any clip size).
//
//   POST /api/kids/pack/presign  { name, filename, contentType? } → { url, key, expiresAt }
//   then: PUT <url> with the raw file bytes (direct to R2)
//
// The packer reads the bucket at `input/<First Last>/<original filename>` — the
// ORIGINAL filename is preserved because the packer derives the lighting order
// from tokens inside the name. (KidsPackClipNameSchema + KidsPackClipResSchema
// below are retained for the legacy through-worker PUT variant / reuse.)
import { z } from 'zod';

// Participant name carried in the X-Clip-Name header (URL-decoded on the worker).
// Latin letters + space, apostrophe, hyphen — the full "First Last" string. No
// slashes / dots-runs so it can never escape the `input/<name>/` R2 prefix.
const CLIP_NAME_RE = /^[A-Za-z][A-Za-z '\-]*$/;
export const KidsPackClipNameSchema = z.string().trim().min(1).max(120).regex(CLIP_NAME_RE);

export const KidsPackClipResSchema = z.object({
  ok: z.literal(true),
  key: z.string(), // the R2 key the clip landed at: input/<First Last>/<filename>
  size: z.number().int().nonnegative().optional(), // bytes, when known
});
export type KidsPackClipRes = z.infer<typeof KidsPackClipResSchema>;

// ── Presigned PUT (variant A — browser uploads straight to R2) ────────────────
//   POST /api/kids/pack/presign  { name, filename, contentType?, sessionId? }
//   → { url, key, expiresAt }   (url = short-lived presigned R2 PUT URL)
// The clip bytes never touch the worker (no 100 MB request-body cap, any size).
// name/filename are in the JSON BODY (not the URL) — bodies aren't logged.
//
// `sessionId` is OPTIONAL: when the moderator PICKED a participant from the
// person picker we send the session id so the worker can namespace the R2 folder
// (`input/<name>__<sessionId>/…`), disambiguating two participants with the SAME
// name. When the name was FREE-TYPED (no candidate picked) there's no session id
// → the field is omitted and the worker falls back to the plain `input/<name>/…`
// layout (backward compatible).
export const KidsPackPresignReqSchema = z.object({
  name: KidsPackClipNameSchema, // normalized "First Last"
  filename: z.string().trim().min(1).max(200), // original; worker strips path-traversal
  contentType: z.string().trim().max(120).optional(), // video/* mime (advisory)
  sessionId: z.number().int().positive().optional(), // sessions.id — namespaces same-name participants
});
export type KidsPackPresignReq = z.infer<typeof KidsPackPresignReqSchema>;

export const KidsPackPresignResSchema = z.object({
  url: z.string().url(), // presigned R2 PUT URL — client PUTs the file bytes here
  key: z.string(), // resolved R2 key: input/<First Last>/<filename>
  expiresAt: z.number().int().positive(), // epoch ms when the URL stops working
});
export type KidsPackPresignRes = z.infer<typeof KidsPackPresignResSchema>;

// ── Multipart upload (variant A2 — resumable clip PUT in 10 MB parts) ──────────
// Large clips (50–200 MB) over a mobile uplink drop mid-PUT and a single presigned
// PUT restarts the WHOLE file. Multipart splits a file into ~10 MB parts that go
// THROUGH the worker (each part is a small request-body, well under the 100 MB
// cap): an interrupted part retries from that part, not the whole clip.
//
//   POST /api/kids/pack/mpu-create   { name, filename, contentType?, sessionId? }
//        → { key, uploadId }            (R2.createMultipartUpload — same key as presign)
//   POST /api/kids/pack/mpu-part      RAW body = part bytes; metadata in headers
//        X-Mpu-Key (URL-encoded) · X-Mpu-Upload-Id · X-Mpu-Part (1-based)
//        → { partNumber, etag }         (binary endpoint — NO zod body contract)
//   POST /api/kids/pack/mpu-complete  { key, uploadId, parts: [{partNumber, etag}] }
//        → { ok }                       (R2.complete — assembles the object)
//   POST /api/kids/pack/mpu-abort      { key, uploadId } → { ok }   (discard leftovers)
//
// The mpu-part endpoint is BINARY (raw bytes in / small JSON out): per contracts.md
// §"Don'ts", binary endpoints get no Req/Res body schema — the metadata lives in
// headers (NOT the URL: X-Mpu-Key is PII-bearing, secrets.md keeps it off query
// strings/logs) and is hand-validated in the handler. create/complete/abort are
// plain JSON and get full Req/Res contracts below.
//
// mpu-create reuses KidsPackPresignReqSchema's shape verbatim (name/filename/
// contentType?/sessionId?) — same key-building inputs — so we alias it rather than
// re-declaring, keeping the two entry points in lockstep.
export const KidsPackMpuCreateReqSchema = KidsPackPresignReqSchema;
export type KidsPackMpuCreateReq = z.infer<typeof KidsPackMpuCreateReqSchema>;

export const KidsPackMpuCreateResSchema = z.object({
  key: z.string(), // resolved R2 key: input/<First Last>[__<sessionId>]/<filename>
  uploadId: z.string(), // R2 multipart upload id — threaded into every part + complete/abort
});
export type KidsPackMpuCreateRes = z.infer<typeof KidsPackMpuCreateResSchema>;

// One uploaded part's receipt (partNumber is 1-based, etag from R2.uploadPart).
export const KidsPackMpuPartSchema = z.object({
  partNumber: z.number().int().positive(),
  etag: z.string().min(1),
});
export type KidsPackMpuPart = z.infer<typeof KidsPackMpuPartSchema>;

export const KidsPackMpuCompleteReqSchema = z.object({
  key: z.string().trim().min(1).max(400),
  uploadId: z.string().trim().min(1).max(400),
  // R2 requires ≥1 part; a 200 MB clip in 10 MB parts is ~20 parts, cap generous.
  parts: z.array(KidsPackMpuPartSchema).min(1).max(10000),
});
export type KidsPackMpuCompleteReq = z.infer<typeof KidsPackMpuCompleteReqSchema>;

export const KidsPackMpuCompleteResSchema = z.object({ ok: z.literal(true) });
export type KidsPackMpuCompleteRes = z.infer<typeof KidsPackMpuCompleteResSchema>;

export const KidsPackMpuAbortReqSchema = z.object({
  key: z.string().trim().min(1).max(400),
  uploadId: z.string().trim().min(1).max(400),
});
export type KidsPackMpuAbortReq = z.infer<typeof KidsPackMpuAbortReqSchema>;

export const KidsPackMpuAbortResSchema = z.object({ ok: z.literal(true) });
export type KidsPackMpuAbortRes = z.infer<typeof KidsPackMpuAbortResSchema>;

// ── Existing clips of a session folder (upload resume) ───────────────────────
// POST /api/kids/pack/existing { name, sessionId? } → what's ALREADY in the
// session's R2 folder. The shipment UI marks matching local files (same
// filename + byte size) as «уже в облаке» and skips re-uploading them — a
// closed tab / interrupted shipment resumes instead of re-sending hundreds
// of MB over a mobile uplink (03.07).
export const KidsPackExistingReqSchema = z.object({
  name: KidsPackClipNameSchema,
  sessionId: z.number().int().positive().optional(),
});
export type KidsPackExistingReq = z.infer<typeof KidsPackExistingReqSchema>;

export const KidsPackExistingResSchema = z.object({
  files: z.array(z.object({ filename: z.string(), size: z.number().int() })),
});
export type KidsPackExistingRes = z.infer<typeof KidsPackExistingResSchema>;

// ── Upload-error beacon ───────────────────────────────────────────────────────
// POST /api/kids/pack/upload-error — the browser reports a failed clip PUT so
// the cause lands in `wrangler tail` (browser→R2 uploads never touch our worker,
// so without this beacon a moderator's «ошибка» is invisible server-side).
// filename only — no participant name (PII).
export const KidsPackUploadErrorReqSchema = z.object({
  filename: z.string().trim().min(1).max(200),
  code: z.string().trim().min(1).max(60), // network | http_403 | aborted | …
  sessionId: z.number().int().positive().optional(),
});
export type KidsPackUploadErrorReq = z.infer<typeof KidsPackUploadErrorReqSchema>;

// ── Mark-shipped (counter semantics, Tim 02.07) ───────────────────────────────
//   POST /api/kids/pack/mark-shipped { sessionIds } → { ok, updated }
// Stamps sessions.uploaded_at for clip groups the pack flow just finished
// uploading to R2 — drives the "отгружено" counter (clips in the cloud), which
// is NOT `packed` (the packer's ship-to-client flag). Idempotent: already-stamped
// ids skipped; moderators may only stamp their own sessions.
export const KidsPackMarkShippedReqSchema = z.object({
  sessionIds: z.array(z.number().int().positive()).min(1).max(100),
  // Способ отгрузки для mod_activity_log (Tim 21.07): auto = «как в ТГ»,
  // manual = ручная раскладка, byname = «Авто по именам», byface = метчинг по
  // лицу. Optional — старые клиенты шлют без него (событие уйдёт с method NULL).
  method: z.enum(['auto', 'manual', 'byname', 'byface']).optional(),
});
export type KidsPackMarkShippedReq = z.infer<typeof KidsPackMarkShippedReqSchema>;

export const KidsPackMarkShippedResSchema = z.object({
  ok: z.literal(true),
  updated: z.number().int(),
});
export type KidsPackMarkShippedRes = z.infer<typeof KidsPackMarkShippedResSchema>;

// ── Pre-ship name check (variant A guard) ─────────────────────────────────────
//   POST /api/kids/pack/check-names  { names: string[] }  → { found, missing }
// Before uploading, the moderator's group names ("First Last", normalized) are
// matched against existing (non-canceled) sessions' children.name in the caller's
// office. A group whose name has no session would land its clips in R2 with
// nothing to join to, so we surface those `missing` names BEFORE the upload runs.
// Names go in the JSON BODY (PII off the URL/log); each is the same latin
// "First Last" the presign endpoint accepts.
export const KidsPackCheckNamesReqSchema = z.object({
  names: z.array(KidsPackClipNameSchema).min(1).max(100), // normalized "First Last", deduped client-side
});
export type KidsPackCheckNamesReq = z.infer<typeof KidsPackCheckNamesReqSchema>;

export const KidsPackCheckNamesResSchema = z.object({
  found: z.array(z.string()),   // names with a matching session in the office
  missing: z.array(z.string()), // names with no matching session — create a session / fix spelling
});
export type KidsPackCheckNamesRes = z.infer<typeof KidsPackCheckNamesResSchema>;

// ── Pack candidates (searchable person picker source) ─────────────────────────
//   GET /api/kids/pack/candidates → { candidates: [{ name, sessionId }, …] }
// Feeds the "who does this group belong to" picker in the shipping form: the
// moderator types → the list filters → picking a row substitutes the CANONICAL
// `children.name` (exactly the packer join key), so a shipment can't miss over a
// typo/casing drift. Scope mirrors check-names: manager → all offices;
// admin/moderator → own office; non-canceled sessions only. Each candidate shows
// the session id next to the name (disambiguates same-name participants). Names
// are PII but this is an authenticated GET returning only in-scope participants;
// the worker never logs the list. `name` is the same latin "First Last".
export const KidsPackCandidateSchema = z.object({
  name: z.string(), // canonical children.name — the packer join key ("First Last")
  sessionId: z.number().int().positive(), // sessions.id (numeric) for display + disambiguation
  // Participant identity + optional reference-photo flag (#35 / B12): childId lets
  // the picker fetch the thumbnail via GET /api/kids/participant-photo/:childId
  // (staff-authed stream); hasPhoto is true when children.photo_key is set, so the
  // UI only renders an <img> when there's actually a photo to show. The R2 key is
  // NEVER sent to the client (secrets.md) — only childId + the boolean.
  childId: z.string(), // children.id — used to build the photo stream URL
  hasPhoto: z.boolean().default(false), // true ⇒ children.photo_key is set
});
export type KidsPackCandidate = z.infer<typeof KidsPackCandidateSchema>;

export const KidsPackCandidatesResSchema = z.object({
  candidates: z.array(KidsPackCandidateSchema),
});
export type KidsPackCandidatesRes = z.infer<typeof KidsPackCandidatesResSchema>;
